// The account the trader gets: its size, its leverage and how positions net.

import { For, Show } from 'solid-js';

import { ACCOUNT_SIZES, cleanNumber, sizeLabel } from '../core/format';
import type { CustomChallengeBuilderInput } from '../form';
import Dropdown from './Dropdown';
import NumberInput from './NumberInput';
import { useMoneyMark } from './builderWordsContext';

interface AccountCardProps {
  form: CustomChallengeBuilderInput;
  onUpdate: <K extends keyof CustomChallengeBuilderInput>(key: K, value: CustomChallengeBuilderInput[K]) => void;
}

export default function AccountCard(props: AccountCardProps) {
  const picked = () => cleanNumber(props.form.accountSize);
  const mark = useMoneyMark();
  return (
    <section class="ch-card">
      <h3 class="ch-card-title">Account</h3>
      <div class="ch-chips" role="group" aria-label="Account size">
        <For each={ACCOUNT_SIZES}>
          {(size) => (
            <button
              class="ch-chip"
              type="button"
              aria-pressed={picked() === size}
              onClick={() => props.onUpdate('accountSize', size)}
            >
              {sizeLabel(size, props.form.currency)}
            </button>
          )}
        </For>
      </div>
      <div class="ch-grid-3">
        <div class="ch-field">
          <span class="ch-label">Account size</span>
          <NumberInput
            value={props.form.accountSize}
            onInput={(value) => props.onUpdate('accountSize', value)}
            prefix={mark}
            min={1000}
            step={1000}
            required
          />
        </div>
        <div class="ch-field">
          <span class="ch-label">Leverage</span>
          <NumberInput
            value={props.form.leverage}
            onInput={(value) => props.onUpdate('leverage', value)}
            min={1}
            step={1}
            required
          />
        </div>
        <div class="ch-field">
          <span class="ch-label">Opposite positions</span>
          <Dropdown
            value={props.form.positionMode}
            options={[
              { value: 'netting', label: 'Net against each other' },
              { value: 'hedging', label: 'Stay open side by side' },
            ]}
            onChange={(value) => props.onUpdate('positionMode', value === 'hedging' ? 'hedging' : 'netting')}
          />
        </div>
        <Show when={props.form.positionMode === 'hedging'}>
          <div class="ch-field">
            <span class="ch-label">Margin on a hedge</span>
            <Dropdown
              value={props.form.hedgeMarginMethod}
              options={[
                { value: 'gross', label: 'Both legs in full' },
                { value: 'largest_leg', label: 'The largest leg only' },
                { value: 'offset', label: 'A share of the offset' },
              ]}
              onChange={(value) =>
                props.onUpdate('hedgeMarginMethod', value as CustomChallengeBuilderInput['hedgeMarginMethod'])}
            />
          </div>
        </Show>
        <Show when={props.form.positionMode === 'hedging' && props.form.hedgeMarginMethod === 'offset'}>
          <div class="ch-field">
            <span class="ch-label">Offset factor</span>
            <NumberInput
              value={props.form.hedgeOffsetFactor}
              onInput={(value) => props.onUpdate('hedgeOffsetFactor', value)}
              min={0}
              max={1}
              step={0.01}
              required
            />
          </div>
        </Show>
      </div>
    </section>
  );
}
